import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { jobOrdersAPI, customersAPI, letterheadsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  FileText,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Building2,
  MapPin,
  Phone,
  Calendar,
  Wrench,
  ClipboardList,
  ArrowUpDown,
  Printer,
  Clock,
} from "lucide-react";

const JOB_STATUSES = [
  { value: "draft", label: "Draft", color: "bg-slate-100 text-slate-800 border-slate-300" },
  { value: "in_progress", label: "In Progress", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "completed", label: "Completed", color: "bg-green-100 text-green-800 border-green-300" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-800 border-red-300" },
];

const JOB_TYPES = [
  { id: "rental", label: "Rental" },
  { id: "chargeable", label: "Chargeable" },
  { id: "warranty", label: "Warranty" },
  { id: "servicing", label: "Servicing" },
  { id: "sales", label: "Sales" },
];

const initialFormData = {
  order_date: new Date().toISOString().split("T")[0],
  letterhead: "none",
  customer_id: "",
  customer_name: "",
  location: "",
  contact_person: "",
  tel_no: "",
  make: "",
  model: "",
  serial_no: "",
  hour_meter: "",
  job_description: "",
  job_done: "",
  time_entries: [{ date: "", time_in: "", time_out: "", hours: "" }],
  job_type_rental: false,
  job_type_chargeable: false,
  job_type_warranty: false,
  job_type_servicing: false,
  job_type_sales: false,
  remarks: "",
  technician_name: "",
  customer_cert_name: "",
  status: "draft",
};

const JobOrdersPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [letterheads, setLetterheads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState("order_date");
  const [sortOrder, setSortOrder] = useState("desc");

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const canManage = isAdmin || isManager;

  useEffect(() => {
    fetchOrders();
    fetchCustomers();
    fetchLetterheads();
  }, [page, filterStatus, filterType, dateFrom, dateTo]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        search: searchTerm || undefined,
        status: filterStatus || undefined,
        job_type: filterType || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const response = await jobOrdersAPI.getAll(params);
      setOrders(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to fetch job orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchLetterheads = async () => {
    try {
      const response = await letterheadsAPI.getAll();
      setLetterheads(response.data);
    } catch (error) {
      console.error("Failed to fetch letterheads:", error);
    }
  };

  const getLetterheadImage = (letterheadId) => {
    if (!letterheadId || letterheadId === "none") return null;
    const lh = letterheads.find((l) => l.id === letterheadId);
    return lh?.image_data || null;
  };

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      // Sort customers alphabetically by company name
      const sortedCustomers = response.data.sort((a, b) => {
        const nameA = (a.company || a.name || "").toLowerCase();
        const nameB = (b.company || b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setCustomers(sortedCustomers);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const handleOpenDialog = (order = null) => {
    if (order) {
      setEditingOrder(order);
      setFormData({
        order_date: order.order_date || "",
        letterhead: order.letterhead || "poh_li_heng",
        customer_id: order.customer_id || "",
        customer_name: order.customer_name || "",
        location: order.location || "",
        contact_person: order.contact_person || "",
        tel_no: order.tel_no || "",
        make: order.make || "",
        model: order.model || "",
        serial_no: order.serial_no || "",
        hour_meter: order.hour_meter || "",
        job_description: order.job_description || "",
        job_done: order.job_done || "",
        time_entries: order.time_entries?.length > 0 
          ? order.time_entries 
          : [{ date: "", time_in: "", time_out: "", hours: "" }],
        job_type_rental: order.job_type_rental || false,
        job_type_chargeable: order.job_type_chargeable || false,
        job_type_warranty: order.job_type_warranty || false,
        job_type_servicing: order.job_type_servicing || false,
        job_type_sales: order.job_type_sales || false,
        remarks: order.remarks || "",
        technician_name: order.technician_name || "",
        customer_cert_name: order.customer_cert_name || "",
        status: order.status || "draft",
      });
    } else {
      setEditingOrder(null);
      setFormData({
        ...initialFormData,
        order_date: new Date().toISOString().split("T")[0],
        technician_name: user?.name || "",
      });
    }
    setDialogOpen(true);
  };

  const handleViewOrder = (order) => {
    setViewingOrder(order);
    setViewDialogOpen(true);
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      setFormData({
        ...formData,
        customer_id: customerId,
        customer_name: customer.company || customer.name || "",
        location: customer.address || "",
        tel_no: customer.phone || "",
        contact_person: customer.name || "",
      });
    } else {
      setFormData({ ...formData, customer_id: customerId });
    }
  };

  const handleTimeEntryChange = (index, field, value) => {
    const newEntries = [...formData.time_entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    
    // Auto-calculate hours if time_in and time_out are set
    if (field === "time_in" || field === "time_out") {
      const entry = newEntries[index];
      if (entry.time_in && entry.time_out) {
        const [inH, inM] = entry.time_in.split(":").map(Number);
        const [outH, outM] = entry.time_out.split(":").map(Number);
        const hours = (outH + outM / 60) - (inH + inM / 60);
        if (hours > 0) {
          newEntries[index].hours = hours.toFixed(1);
        }
      }
    }
    
    setFormData({ ...formData, time_entries: newEntries });
  };

  const addTimeEntry = () => {
    setFormData({
      ...formData,
      time_entries: [...formData.time_entries, { date: "", time_in: "", time_out: "", hours: "" }]
    });
  };

  const removeTimeEntry = (index) => {
    if (formData.time_entries.length > 1) {
      const newEntries = formData.time_entries.filter((_, i) => i !== index);
      setFormData({ ...formData, time_entries: newEntries });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const submitData = {
        ...formData,
        time_entries: formData.time_entries.filter(e => e.date || e.time_in || e.time_out)
      };
      
      if (editingOrder) {
        await jobOrdersAPI.update(editingOrder.id, submitData);
        toast.success("Job Order updated successfully");
      } else {
        await jobOrdersAPI.create(submitData);
        toast.success("Job Order created successfully");
      }
      setDialogOpen(false);
      setEditingOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (orderId) => {
    try {
      await jobOrdersAPI.delete(orderId);
      toast.success("Job Order deleted successfully");
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete job order");
    }
  };

  const handleDownloadPdf = async (order) => {
    try {
      toast.info("Generating PDF...");
      await jobOrdersAPI.downloadPdf(order.id, order.order_no);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to generate PDF");
    }
  };

  const handleExport = async () => {
    try {
      toast.info("Exporting Job Orders...");
      await jobOrdersAPI.exportXlsx({
        status: filterStatus || undefined,
        job_type: filterType || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      toast.success("Export completed");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = JOB_STATUSES.find((s) => s.value === status) || JOB_STATUSES[0];
    return (
      <Badge className={`${statusConfig.color} rounded-none uppercase text-xs font-bold tracking-wide border`}>
        {statusConfig.label}
      </Badge>
    );
  };

  const getJobTypeBadges = (order) => {
    const types = [];
    if (order.job_type_rental) types.push("Rental");
    if (order.job_type_chargeable) types.push("Chargeable");
    if (order.job_type_warranty) types.push("Warranty");
    if (order.job_type_servicing) types.push("Servicing");
    if (order.job_type_sales) types.push("Sales");
    
    return types.map((type) => (
      <Badge key={type} className="bg-amber-100 text-amber-800 border-amber-300 rounded-none uppercase text-xs font-bold tracking-wide border">
        {type}
      </Badge>
    ));
  };

  const sortedOrders = [...orders].sort((a, b) => {
    let aVal = a[sortBy] || "";
    let bVal = b[sortBy] || "";
    if (sortOrder === "asc") {
      return aVal.localeCompare(bVal);
    }
    return bVal.localeCompare(aVal);
  });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="job-orders-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Job Orders
          </h1>
          <p className="text-slate-500">
            {total} Job Order{total !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="rounded-sm"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-amber-500 text-black font-bold uppercase tracking-wider hover:bg-amber-600 rounded-sm border-2 border-transparent hover:border-black"
            data-testid="create-job-order-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Job Order
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search job orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" className="rounded-sm">
              Search
            </Button>
          </form>
          <div className="flex gap-2">
            <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[140px] rounded-sm" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {JOB_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={(v) => { setFilterType(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[140px] rounded-sm" data-testid="type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Job Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {JOB_TYPES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Date Range Filter */}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Date Range:</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-[150px] rounded-sm h-9"
              data-testid="date-from"
            />
            <span className="text-slate-400">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-[150px] rounded-sm h-9"
              data-testid="date-to"
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
              className="text-slate-500 hover:text-slate-700 rounded-sm h-9"
            >
              Clear dates
            </Button>
          )}
        </div>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <ArrowUpDown className="w-4 h-4" />
        <span>Sort by:</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => toggleSort("order_date")}
          className={`rounded-sm ${sortBy === "order_date" ? "text-amber-600 font-semibold" : ""}`}
        >
          Date {sortBy === "order_date" && (sortOrder === "asc" ? "↑" : "↓")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => toggleSort("order_no")}
          className={`rounded-sm ${sortBy === "order_no" ? "text-amber-600 font-semibold" : ""}`}
        >
          Order No {sortBy === "order_no" && (sortOrder === "asc" ? "↑" : "↓")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => toggleSort("status")}
          className={`rounded-sm ${sortBy === "status" ? "text-amber-600 font-semibold" : ""}`}
        >
          Status {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
        </Button>
      </div>

      {/* Job Order List */}
      {sortedOrders.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No job orders found</p>
            <Button
              onClick={() => handleOpenDialog()}
              variant="link"
              className="mt-2"
            >
              Create your first job order
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedOrders.map((order) => (
            <Card
              key={order.id}
              className="border-slate-200 hover:border-slate-400 transition-colors cursor-pointer"
              onClick={() => handleViewOrder(order)}
              data-testid={`job-order-card-${order.id}`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-bold text-lg text-slate-900">
                        {order.order_no}
                      </h3>
                      {getStatusBadge(order.status)}
                      {getJobTypeBadges(order)}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span>{order.customer_name || "N/A"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>{order.order_date || "No date"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Wrench className="w-4 h-4 text-slate-400" />
                        <span>{order.make} {order.model || "N/A"}</span>
                      </div>
                      {order.location && (
                        <div className="flex items-center gap-2 col-span-full">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="truncate">{order.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewOrder(order)}
                      className="rounded-sm"
                      data-testid={`view-order-${order.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPdf(order)}
                      className="rounded-sm"
                      data-testid={`pdf-order-${order.id}`}
                      title="Download PDF"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(order)}
                      className="rounded-sm"
                      data-testid={`edit-order-${order.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {canManage && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm"
                            data-testid={`delete-order-${order.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Job Order?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete Job Order {order.order_no}. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(order.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="rounded-sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-slate-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="rounded-sm"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingOrder ? `Edit Job Order - ${editingOrder.order_no}` : "New Job Order & D/O"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 pt-4">
            {/* Date & Status Section */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Order Date</Label>
                <Input
                  type="date"
                  value={formData.order_date}
                  onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                  className="rounded-sm"
                  data-testid="jo-order-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Letterhead</Label>
                <Select
                  value={formData.letterhead}
                  onValueChange={(v) => setFormData({ ...formData, letterhead: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="jo-letterhead">
                    <SelectValue placeholder="Select letterhead..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Letterhead</SelectItem>
                    {letterheads.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="jo-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Customer Information Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Customer Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Link to Customer</Label>
                  <Select
                    value={formData.customer_id || "none"}
                    onValueChange={(v) => handleCustomerSelect(v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="jo-customer">
                      <SelectValue placeholder="Select customer..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Manual Entry --</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.company || c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Customer Name</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="rounded-sm"
                    data-testid="jo-customer-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Contact Person</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs text-slate-500">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="rounded-sm"
                    data-testid="jo-location"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Tel No.</Label>
                  <Input
                    value={formData.tel_no}
                    onChange={(e) => setFormData({ ...formData, tel_no: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Equipment Information Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Equipment Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Make</Label>
                  <Input
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    className="rounded-sm"
                    data-testid="jo-make"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Model</Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">S/N No.</Label>
                  <Input
                    value={formData.serial_no}
                    onChange={(e) => setFormData({ ...formData, serial_no: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Hour Meter</Label>
                  <Input
                    value={formData.hour_meter}
                    onChange={(e) => setFormData({ ...formData, hour_meter: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Job Description Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Job / Inspection Description</h3>
              <Textarea
                value={formData.job_description}
                onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                className="rounded-sm min-h-[100px]"
                placeholder="Describe the job or inspection required..."
                data-testid="jo-job-description"
              />
            </div>

            {/* Job Done Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Job Done</h3>
              <Textarea
                value={formData.job_done}
                onChange={(e) => setFormData({ ...formData, job_done: e.target.value })}
                className="rounded-sm min-h-[100px]"
                placeholder="Describe work completed, parts used, pricing..."
                data-testid="jo-job-done"
              />
            </div>

            {/* Time Tracking Section */}
            <div className="border-t border-slate-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold uppercase text-sm tracking-wide text-slate-700">Time Tracking</h3>
                <Button type="button" variant="outline" size="sm" onClick={addTimeEntry} className="rounded-sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Row
                </Button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-5 gap-2 text-xs font-semibold text-slate-500 uppercase">
                  <div>Date</div>
                  <div>Time In</div>
                  <div>Time Out</div>
                  <div>Hours</div>
                  <div></div>
                </div>
                {formData.time_entries.map((entry, index) => (
                  <div key={index} className="grid grid-cols-5 gap-2">
                    <Input
                      type="date"
                      value={entry.date}
                      onChange={(e) => handleTimeEntryChange(index, "date", e.target.value)}
                      className="rounded-sm h-9"
                    />
                    <Input
                      type="time"
                      value={entry.time_in}
                      onChange={(e) => handleTimeEntryChange(index, "time_in", e.target.value)}
                      className="rounded-sm h-9"
                    />
                    <Input
                      type="time"
                      value={entry.time_out}
                      onChange={(e) => handleTimeEntryChange(index, "time_out", e.target.value)}
                      className="rounded-sm h-9"
                    />
                    <Input
                      type="text"
                      value={entry.hours}
                      onChange={(e) => handleTimeEntryChange(index, "hours", e.target.value)}
                      className="rounded-sm h-9"
                      placeholder="0.0"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTimeEntry(index)}
                      className="h-9 text-slate-400 hover:text-red-500"
                      disabled={formData.time_entries.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Job Type Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Job Type</h3>
              <div className="flex flex-wrap gap-6">
                {JOB_TYPES.map((type) => (
                  <div key={type.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`job_type_${type.id}`}
                      checked={formData[`job_type_${type.id}`]}
                      onCheckedChange={(checked) => setFormData({ ...formData, [`job_type_${type.id}`]: checked })}
                    />
                    <label htmlFor={`job_type_${type.id}`} className="text-sm cursor-pointer">
                      {type.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Remarks & Signatures Section */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold uppercase text-sm tracking-wide mb-3 text-slate-700">Remarks & Signatures</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Remarks</Label>
                  <Textarea
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    className="rounded-sm min-h-[80px]"
                  />
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Technician Name</Label>
                    <Input
                      value={formData.technician_name}
                      onChange={(e) => setFormData({ ...formData, technician_name: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Customer Certification Name</Label>
                    <Input
                      value={formData.customer_cert_name}
                      onChange={(e) => setFormData({ ...formData, customer_cert_name: e.target.value })}
                      className="rounded-sm"
                      placeholder="Customer name for certification"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-amber-500 text-black font-bold uppercase hover:bg-amber-600 rounded-sm"
                data-testid="jo-submit"
              >
                {saving ? "Saving..." : editingOrder ? "Update Job Order" : "Create Job Order"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Job Order - {viewingOrder?.order_no}
            </DialogTitle>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-6 pt-4">
              {/* Letterhead */}
              {getLetterheadImage(viewingOrder.letterhead) && (
                <div className="border-b border-slate-200 pb-4">
                  <img
                    src={getLetterheadImage(viewingOrder.letterhead)}
                    alt="Letterhead"
                    className="max-h-24 mx-auto object-contain"
                  />
                </div>
              )}

              {/* Header Info */}
              <div className="flex items-center gap-3 flex-wrap">
                {getStatusBadge(viewingOrder.status)}
                {getJobTypeBadges(viewingOrder)}
                <span className="text-slate-500 text-sm">Date: {viewingOrder.order_date || "N/A"}</span>
              </div>

              {/* Customer & Equipment Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-slate-700">Customer Information</h4>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-slate-500">Customer:</span> {viewingOrder.customer_name || "N/A"}</div>
                    <div><span className="text-slate-500">Location:</span> {viewingOrder.location || "N/A"}</div>
                    <div><span className="text-slate-500">Contact:</span> {viewingOrder.contact_person || "N/A"}</div>
                    <div><span className="text-slate-500">Tel:</span> {viewingOrder.tel_no || "N/A"}</div>
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-slate-700">Equipment Information</h4>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-slate-500">Make:</span> {viewingOrder.make || "N/A"}</div>
                    <div><span className="text-slate-500">Model:</span> {viewingOrder.model || "N/A"}</div>
                    <div><span className="text-slate-500">Serial No:</span> {viewingOrder.serial_no || "N/A"}</div>
                    <div><span className="text-slate-500">Hour Meter:</span> {viewingOrder.hour_meter || "N/A"}</div>
                  </div>
                </div>
              </div>

              {/* Job Description */}
              {viewingOrder.job_description && (
                <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-slate-700">Job / Inspection Description</h4>
                  <p className="text-sm whitespace-pre-wrap">{viewingOrder.job_description}</p>
                </div>
              )}

              {/* Job Done */}
              {viewingOrder.job_done && (
                <div className="bg-amber-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-amber-700">Job Done</h4>
                  <p className="text-sm whitespace-pre-wrap">{viewingOrder.job_done}</p>
                </div>
              )}

              {/* Time Entries */}
              {viewingOrder.time_entries?.length > 0 && viewingOrder.time_entries.some(e => e.date || e.time_in) && (
                <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-slate-700">Time Tracking</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Date</th>
                          <th className="text-left py-2">Time In</th>
                          <th className="text-left py-2">Time Out</th>
                          <th className="text-left py-2">Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingOrder.time_entries.filter(e => e.date || e.time_in).map((entry, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="py-2">{entry.date || "-"}</td>
                            <td className="py-2">{entry.time_in || "-"}</td>
                            <td className="py-2">{entry.time_out || "-"}</td>
                            <td className="py-2">{entry.hours || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Remarks */}
              {viewingOrder.remarks && (
                <div className="bg-slate-50 p-4 rounded-sm space-y-2">
                  <h4 className="font-semibold text-sm uppercase text-slate-700">Remarks</h4>
                  <p className="text-sm whitespace-pre-wrap">{viewingOrder.remarks}</p>
                </div>
              )}

              {/* Footer */}
              <div className="text-sm text-slate-500 flex flex-wrap justify-between gap-4">
                <span>Technician: {viewingOrder.technician_name || "N/A"}</span>
                <span>Customer: {viewingOrder.customer_cert_name || "N/A"}</span>
                <span>Created: {new Date(viewingOrder.created_at).toLocaleDateString()}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => setViewDialogOpen(false)}
                  className="flex-1 rounded-sm"
                >
                  Close
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPdf(viewingOrder)}
                  className="flex-1 rounded-sm"
                  data-testid="view-pdf-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleOpenDialog(viewingOrder);
                  }}
                  className="flex-1 bg-slate-800 text-white hover:bg-slate-900 rounded-sm"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobOrdersPage;
